'use strict';

/* global angular, L */

const _ = require('lodash');


// Constant definition section

const DOWNLOAD_LABEL = 'Download';
const DEFAULT_LABEL = 'Export';

const DEFAULT_POSITION = 'center';
const DEFAULT_QUALITY = 'normal';
const DEFAULT_SAVE_DESTINATION = 'computer';
const DEFAULT_SITE_VIEW = 'currentView';
const FULL_SITE_VIEW = 'fullSite';

const MAX_ZOOM = 21;

const AUTODESK_SAVE_DESTINATION = 'autodesk';
const ORIGIN = 'core';
const PDF_TYPE = 'pdf';


// Private constants, should not be used outside this service

const _SLUG_AC2 = 'ac2';
const _SLUG_WORKDAY = 'workday';
const _REMOVED_COMMON_FIELDS = ['_permissions', 'site'];
const _REMOVED_SITE_FIELDS = ['_permissions', 'annotations', 'dataCaptures', 'overlays', 'photoSets', 'project', 'measurements', 'migrationId'];
const _TABLOID_RATIO = 1.54;

/**
 * This Service is the base class for the different PdfPreview Services that can
 * be instantiated depending if the context is the dashboard or the dataset
 * manager. The PdfPreviewController determines at instantiation time its
 * current context so it can use the corresponding service
 * @class PdfPreviewService
 */
class PdfPreviewService {


  // --------------------------------
  // --- Override methods section ---
  // --------------------------------

  getSite () {

    throw 'Not overriden';
  }

  getDataCapture () {

    throw 'Not overriden';
  }

  getAnnotations () {

    throw 'Not overriden';
  }

  getOverlays () {

    throw 'Not overriden';
  }

  getMeasurements () {

    throw 'Not overriden';
  }

  // ------------------------
  // --- Instance methods ---
  // ------------------------

  /**
   * Utility method for getting constants since ES6 classes don't support
   * class variables
   */
  getConstant (constantName) {
    return this._getConstants()[constantName];
  }

  /**
   * Returns the formatted dataCaptures as the dataExports model expects it to
   * be passed to the backend
   * @returns {Object}
   */
  getFormattedDataCaptures () {

    let dataCapture = this.getCurrentDataCapture();
    if (!dataCapture) {
      return [];
    }
    dataCapture.layers = angular.copy(dataCapture.dataCaptureLayers);
    dataCapture.layers.forEach((layer) => {
      layer.dataCaptureId = dataCapture.id;
      layer.layerId = layer.layerData;
    });

    return [dataCapture];
  }

  /**
   * Retrieves the dataExports associated with the specified dataCapture, if any
   * @param {integer} id The id of the specified dataCapture
   * @returns {Object} Object with download Url and label for the existing file
   */
  getDirectDownloadData (id) {

    let downloadUrl, label;

    const DataCaptureStore = this.Flux.Store.get('DataCaptureStore');
    return DataCaptureStore.getRelations(id)
    .then((dc) => {
      const pdfExport = dc.dataExports.filter((dataExport) => {
        return dataExport.location && dataExport.location.slice(-3) === PDF_TYPE;
      });

      const location = pdfExport.length > 0 ? pdfExport[0].location : null;
      if (location) {
        downloadUrl = location;
        label = DOWNLOAD_LABEL;
      }
      else {
        // we have to reset these or the controller will always retain the download state and url of the first datacapture that sets them
        downloadUrl = '';
        label = this.getConstant('DEFAULT_LABEL');
      }

      return {
        // TODO: to change this, we'd have to change where this is saved and migrate our data
        downloadUrl: downloadUrl.replace('http://s3-us-west-1.amazonaws.com/skycatch-pdf/', 'https://d1xes4epbabnon.cloudfront.net/'),
        label: label
      };
    });
  }

  /**
   * Create default title for PDF
   * @param   {Object} site current site
   * @param   {Object} dataCapture current data capture
   * @returns {string} default title
   */
  getTitle (site, dataCapture) {

    return [this._getSiteName(site), this._getDate(dataCapture)].join('-');
  }

  isDownloadAvailable (label) {

    return label === this.getConstant('DOWNLOAD_LABEL');
  }

  isFullSiteSelected (view) {

    return view === this.getConstant('FULL_SITE_VIEW');
  }

  hasFullSite (site) {

    // to add full site capability to a site, add its slug to the sites array
    let sites = [_SLUG_AC2, _SLUG_WORKDAY];

    return sites.indexOf(site.slug) > -1;
  }


  /**
   * sanitizeResource removes certain duplicated fields from our various layers
   * to make them smaller and improve performance.
   * @param   {obj || arr} resource an object (site) or array (everything else) to sanitize
   * @returns {obj || arr} sanitizedResource the sanitized version of the resource
   */
  sanitizeResource (resource) {

    let self = this;
    // annotations, datacaptures and overlays are arrays
    if (angular.isArray(resource)) {
      return resource.map((resource) => {
        // sanitize the object's layers as well as the top-level object
        if (resource.layers) {
          resource.layers = resource.layers.map((layer) => {
            return self._sanitize(layer, _REMOVED_COMMON_FIELDS);
          });
        }

        if (resource.dataCaptureLayers) {
          resource.dataCaptureLayers = resource.dataCaptureLayers.map((layer) => {
            return self._sanitize(layer, _REMOVED_COMMON_FIELDS);
          });
        }

        return self._sanitize(resource, _REMOVED_COMMON_FIELDS);
      });
    // site is an object
    }
    else if (angular.isObject(resource)) {
      return self._sanitize(resource, _REMOVED_SITE_FIELDS);
    }
    else {
      // Handle unexpected case
      return resource;
    }
  }

  sendAnalytics (pdfData) {

    this.AnalyticsService.track('[CORE] PDF', 'Resolution Setting', {
      resolution: pdfData.quality,
      siteId: pdfData.site.id,
      siteName: pdfData.site.name
    });

    if (pdfData.fullSite) {
      this.AnalyticsService.track('[CORE] PDF', 'Full Site Export', {
        view: 'full site',
        siteId: pdfData.site.id,
        siteName: pdfData.site.name
      });
    }
    else {
      this.AnalyticsService.track('[CORE] PDF', 'Current View Export', {
        view: 'current view',
        siteId: pdfData.site.id,
        siteName: pdfData.site.name
      });
    }
  }

  setOpacities (map, opacities) {

    map.eachLayer(function (layer) {
      if (layer._url) {
        if (layer._url.indexOf('mapbox') > -1) {
          layer.setOpacity(opacities.mapOpacity);
        }
        else if (layer._url.indexOf('data_captures') > -1) {
          layer.setOpacity(opacities.dcOpacity);
        }
      }
    });
  }

  /**
   * Determines map boundaries needed to fit the desired aspect ratio
   * @param  {object} map        map layer
   * @param  {number} paperRatio the aspect ratio as a number. optional - if not set, function defaults to 11 x 17
   * @return {object} res        object containing latLng of NW and SE points, plus orientation, height and width
   */
  getBounds (map, paperRatio) {

    let res = {};
    let ratio = paperRatio || _TABLOID_RATIO;
    let container = map.getContainer();

    let minRatio = 7/5;
    let height = container.offsetHeight; // $.height() returns the wrong height
    let width = container.offsetWidth; // $.width() return the wrong width

    res.zoom = map.getZoom();
    res.center = map.getCenter();
    res.ratio = ratio;

    // values to be calculated
    let targetWidth, targetHeight, x1, y1, x2, y2, targetNW, targetSE;

    // Landscape mode
    if (width > height) {
      res.orientation = 'landscape';

      // We need to tweak the width otherwise we will cut off too much
      if (width / height < minRatio) {
        targetWidth = width * minRatio;
      }
      else {
        targetWidth = width;
      }

      targetHeight = targetWidth / ratio;

      // calculate the NW pixel coords
      x1 = (width - targetWidth) / 2;
      y1 = (height - targetHeight) / 2;

      // calculate the SE pixel coords
      x2 = x1 + targetWidth;
      y2 = y1 + targetHeight;
    }
    // Portrait mode
    else {
      res.orientation = 'portrait';

      // We need to tweak the height otherwise we will cut off too much
      if (height / width < minRatio) {
        targetHeight = height * minRatio;
      }
      else {
        targetHeight = height;
      }

      targetWidth = targetHeight / ratio;

      // calculate the NW pixel coords
      x1 = (width - targetWidth) / 2;
      y1 = (height - targetHeight) / 2;

      // calculate the SE pixel coords
      x2 = x1 + targetWidth;
      y2 = y1 + targetHeight;
    }

    targetNW = map.containerPointToLatLng(L.point(x1, y1));
    targetSE = map.containerPointToLatLng(L.point(x2, y2));

    res.nw = targetNW;
    res.se = targetSE;
    res.h = targetHeight;
    res.w = targetWidth;

    return res;
  }

  createFileName (inputName) {

    return [Date.now(), inputName].join('-');
  }


  // TODO: Deprecate these functions - Standarize full site support check and bounds.
  isAC2 (slug) {

    return slug === _SLUG_AC2;
  }

  isWorkday (slug) {

    return slug === _SLUG_WORKDAY;
  }

  createFullSiteBoundsForWorkday (dc) {

    let width, height, zoom, orientation, nw, se, center;
    const tileLayer = _.find(dc.dataCaptureLayers, (layer) => {
      return layer.layerType === 'tileLayer';
    });

    if (tileLayer.hasOwnProperty('bounds') && tileLayer.bounds) {
      width = 700;
      height = width * _TABLOID_RATIO;
      zoom = 18;
      orientation = 'portrait';
      nw = tileLayer.bounds[1];
      se = tileLayer.bounds[0];
      center = {
        lat: (nw[0] + se[0]) / 2,
        lng: (nw[1] + se[1]) / 2
      };
    }
    else {
      width = 879;
      height = 570.7792207792207;
      zoom = 17;
      orientation = 'landscape';
      nw = [37.70077774357416, -121.93001389503478];
      se = [37.69593234401553, -121.92058324813841];
      center = {
        lat: 37.69835932792829,
        lng: -121.9252985715866
      };
    }


    const bounds = {
      nw: L.latLng(nw[0], nw[1]),
      se: L.latLng(se[0], se[1]),
      center: L.latLng(center.lat, center.lng),
      zoom,
      h: height,
      w: width,
      ratio: _TABLOID_RATIO,
      orientation
    };

    return bounds;
  }


  // -------------------------------
  // --- Private methods section ---
  // -------------------------------

  _getConstants() {

    // Use shorthand property notation
    return {
      DOWNLOAD_LABEL,
      AUTODESK_SAVE_DESTINATION,
      DEFAULT_LABEL, DEFAULT_POSITION, DEFAULT_QUALITY,
      DEFAULT_SAVE_DESTINATION, DEFAULT_SITE_VIEW, FULL_SITE_VIEW,
      MAX_ZOOM, ORIGIN, PDF_TYPE
    };
  }

  /**
   * creates date to be used in default pdf title. it uses the data capture's captureAt property if it exists or else it defaults to the current date
   * @param   {object} dc current datacapture layer
   * @returns {string}    default date
   */
  _getDate (dc) {

    let date;

    if (dc && dc.capturedAt) {
      // only return the date and discard the time
      date = dc.capturedAt.split('T');

      return date[0];
    }
    else {
      date = new Date();
      // month is 0 indexed so add 1 to get the human readable month
      let month = date.getMonth() + 1;

      // ensure that all months display as two digits
      if (month < 10) {
        month = '0' + month;
      }

      return [date.getFullYear(), month, date.getDate()].join('-');
    }
  }

  /**
   * remove spaces from site name
   * @param   {object} site current site
   * @returns {string} site name without spaces
   */
  _getSiteName () {

    return this.getSite().name.split(' ').join('-');
  }

  _sanitize (resource, keys) {

    let result = _.cloneDeep(resource);
    return _.omit(result, keys);
  }

}

PdfPreviewService.$inject = [];

module.exports = PdfPreviewService;



// WEBPACK FOOTER //
// ./src/app/shared/pdfPreview/services/PdfPreviewService.js